import { newId } from "../../ids.js";
import { isoString, systemClock, type Clock } from "../../time.js";
import type { DeviceMutState } from "../../sync/device-local.js";
import type { Priority, Status } from "../../tasks/types.js";

export type OverrideRange = "single" | "thisandfuture";
export type OverrideStatus = Extract<Status, "open" | "done" | "snoozed"> | "cancelled";

/**
 * RFC 5545 RECURRENCE-ID + EXCEPTION row. One per series edit.
 * NULL fields inherit from the base task; non-null fields override.
 * `recurrence_id_ref` is the original occurrence ISO captured at edit time
 * and never recomputed — that's the stable RECURRENCE-ID.
 */
export interface RecurrenceOverride extends DeviceMutState {
  id: string;
  recurrence_id: string;
  recurrence_id_ref: string;
  range: OverrideRange;
  status: OverrideStatus | null;
  /** Wake time when this occurrence is snoozed (status='snoozed'); null otherwise. */
  snoozed_until: string | null;
  due_at: string | null;
  duration_minutes: number | null;
  title: string | null;
  notes: string | null;
  priority: Priority | null;
  custom_color: string | null;
  all_day: boolean | null;
  /** Only set when range='thisandfuture' AND the user changed the schedule shape. */
  rrule: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface OverridePatch {
  recurrence_id: string;
  recurrence_id_ref: string;
  range: OverrideRange;
  status?: OverrideStatus | null;
  snoozed_until?: string | null;
  due_at?: string | null;
  duration_minutes?: number | null;
  title?: string | null;
  notes?: string | null;
  priority?: Priority | null;
  custom_color?: string | null;
  all_day?: boolean | null;
  rrule?: string | null;
}

export function createOverride(input: OverridePatch, now: Clock = systemClock): RecurrenceOverride {
  const ts = isoString(now());
  return {
    id: newId(),
    recurrence_id: input.recurrence_id,
    recurrence_id_ref: input.recurrence_id_ref,
    range: input.range,
    status: input.status ?? null,
    snoozed_until: input.snoozed_until ?? null,
    due_at: input.due_at ?? null,
    duration_minutes: input.duration_minutes ?? null,
    title: input.title ?? null,
    notes: input.notes ?? null,
    priority: input.priority ?? null,
    custom_color: input.custom_color ?? null,
    all_day: input.all_day ?? null,
    rrule: input.rrule ?? null,
    created_at: ts,
    updated_at: ts,
    deleted_at: null,
  };
}
